import Dialog from "sap/m/Dialog";
import MessageBox from "sap/m/MessageBox";
import DateFormat from "sap/ui/core/format/DateFormat";
import JSONModel from "sap/ui/model/json/JSONModel";
import BaseTypedController from "ips/ac/base/controller/BaseTypedController";
import Util from "ips/ac/util/Util";
import { AppointmentSeriesDto, AppointmentSeriesRepetitionType, DayOfWeek } from "ips/ac/model/api/WinDoFlow.api.types";
import { MultiComboBox$SelectionFinishEventParameters } from "sap/m/MultiComboBox";

const oDateFormat = DateFormat.getDateInstance({ pattern: "yyyy-MM-dd" });
const MAX_RECURRENCE_INTERVAL_WEEKS = 52;
// keeps the number of generated occurrences bounded; mirrors the backend check in AppointmentSeriesValidator
const MAX_SERIES_YEARS_AHEAD = 3;

const WEEKDAY_ORDER = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
  DayOfWeek.SUNDAY,
];

function parseIsoDate(sDate: string): Date {
  return oDateFormat.parse(sDate) as Date;
}

// the backend serializes Timestamp fields as seconds since epoch, not milliseconds
function parseApiTimestamp(value: string | number): Date {
  return typeof value === "number" ? new Date(value * 1000) : new Date(value);
}

// Date#getDay() is 0 (Sunday) ... 6 (Saturday); shift it onto WEEKDAY_ORDER's Monday=0 ... Sunday=6 indexing
function getWeekdayOfDate(date: Date): DayOfWeek {
  return WEEKDAY_ORDER[(date.getDay() + 6) % 7];
}

/**
 * @namespace ips.ac.data.planning.appointment-series.controller
 * */
export default class AppointmentSeriesDialogController extends BaseTypedController {
  private dialog: Dialog;
  // @ts-ignore
  private acceptCallBack: (data: AppointmentSeriesDto) => void;

  onInit(): void {
    super.onInit();
    this.dialog = this.getView().byId("appointmentSeriesDialog") as Dialog;

    const model = new JSONModel({
      daysOfWeek: [],
      startDate: ""
    })
    this.setModel(model, "data")

    const recurrenceIntervalWeeks = Array.from({ length: MAX_RECURRENCE_INTERVAL_WEEKS }, (_, index) => {
      const week = index + 1;
      const text = `${week} ${week === 1 ? Util.getText("Week") : Util.getText("Weeks")}`;
      return { key: String(week), text };
    });
    this.setModel(new JSONModel({ recurrenceIntervalWeeks }), "config");
  }

  public openDialog(
    acceptCallBack: (data: AppointmentSeriesDto) => void,
    appointmentSeries: AppointmentSeriesDto | null | undefined,
    defaultStartDate: Date | string,
  ): void {
    this.acceptCallBack = acceptCallBack;

    const model = this.getModel("data") as JSONModel;
    if (appointmentSeries) {
      model.setData({
        ...appointmentSeries,
        startDate: appointmentSeries.startDate ? oDateFormat.format(parseApiTimestamp(appointmentSeries.startDate)) : "",
        endDate: appointmentSeries.endDate ? oDateFormat.format(parseApiTimestamp(appointmentSeries.endDate)) : "",
      });
    } else {
      model.setData({
        startDate: defaultStartDate ? oDateFormat.format(new Date(defaultStartDate)) : "",
        endDate: "",
        repetitionType: AppointmentSeriesRepetitionType.WEEKLY,
        repetitionInterval: 1,
        daysOfWeek: [],
      });
    }

    this.dialog.open();
  }

  onAccept(): void {
    const model = this.getModel("data") as JSONModel;
    const data = model.getData() as AppointmentSeriesDto;

    if (!this.isValid(data)) {
      MessageBox.error(Util.getText("appointmentSeriesValidationError"));
      return;
    }

    this.acceptCallBack(data);
    this.closeDialog();
  }

  public onWeekdaysSelectionFinish(_event: MultiComboBox$SelectionFinishEventParameters): void {
    const model = this.getModel("data") as JSONModel;
    const data = model.getData() as AppointmentSeriesDto;

    if (data.daysOfWeek && data.daysOfWeek.length > 0) {
      const sortedDaysOfWeek = [...data.daysOfWeek].sort((a, b) => WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b));
      model.setProperty("/daysOfWeek", sortedDaysOfWeek);
    }

    if (!data.startDate || !data.daysOfWeek || data.daysOfWeek.length === 0) {
      return;
    }

    const date = parseIsoDate(data.startDate);
    for (let i = 0; i < 7 && !data.daysOfWeek.includes(getWeekdayOfDate(date)); i++) {
      date.setDate(date.getDate() + 1);
    }

    model.setProperty("/startDate", oDateFormat.format(date));
  }

  closeDialog(): void {
    this.dialog.close();
  }

  // minDate expects an actual Date object, unlike value's string-based sap.ui.model.type.Date binding
  public formatMinDate(sStartDate: string): Date | undefined {
    return sStartDate ? parseIsoDate(sStartDate) : undefined;
  }

  // a series may run at most until December 31st of MAX_SERIES_YEARS_AHEAD years after its start date
  public formatMaxDate(sStartDate: string): Date | undefined {
    if (!sStartDate) {
      return undefined;
    }

    const startDate = parseIsoDate(sStartDate);
    return new Date(startDate.getFullYear() + MAX_SERIES_YEARS_AHEAD, 11, 31);
  }

  private isValid(data: AppointmentSeriesDto): boolean {
    return !!data.startDate
      && !!data.endDate
      && !!data.repetitionType
      && !!data.repetitionInterval
      && !!data.daysOfWeek
      && data.daysOfWeek.length > 0;
  }
}
